import { keys, omit } from 'ramda';
import type { Editor, NodeEntry } from 'slate';
import { Transforms } from 'slate';

import { normalizeAiSuggestionMarkers } from './ai-suggestion-marker-normalizations';
import { ensureUniqueLocationIds } from './location-id-normalizations';
import { mergeWithDefaults } from '../../../util';
import type { DocumentElement } from '../../internal';
import { createDefaultDocElement, documentSchemaVersion } from '../../internal';

type DocumentNormalization = (editor: Editor) => void;

const schemaVersionNormalizations: Record<number, DocumentNormalization[]> = {
  1: [],
  2: [normalizeAiSuggestionMarkers],
  3: [ensureUniqueLocationIds],
};

export function normalizeDocument(editor: Editor, documentEntry: NodeEntry<DocumentElement>): void {
  const [, documentPath] = documentEntry;

  if (documentPath.length > 1) {
    Transforms.removeNodes(editor, { at: documentPath });
    return;
  }

  runSchemaVersionNormalizations(editor, documentEntry);
  validateDocumentProps(editor, documentEntry);
}

function runSchemaVersionNormalizations(
  editor: Editor,
  documentEntry: NodeEntry<DocumentElement>
): void {
  const [document, documentPath] = documentEntry;
  const currentSchemaVersion = document.schemaVersion ?? 0;
  if (currentSchemaVersion >= documentSchemaVersion) return;

  const schemaVersionsToNormalize = getSchemaVersionsToNormalize(currentSchemaVersion);
  if (schemaVersionsToNormalize.length === 0) return;

  for (const schemaVersion of schemaVersionsToNormalize) {
    const normalizations = schemaVersionNormalizations[schemaVersion] ?? [];
    for (const normalize of normalizations) {
      normalize(editor);
    }
  }

  Transforms.setNodes(editor, { schemaVersion: documentSchemaVersion }, { at: documentPath });
}

function getSchemaVersionsToNormalize(currentSchemaVersion: number) {
  return Object.keys(schemaVersionNormalizations)
    .map(Number)
    .filter(
      schemaVersion =>
        currentSchemaVersion < schemaVersion && schemaVersion <= documentSchemaVersion
    )
    .sort((a, b) => a - b);
}

function validateDocumentProps(editor: Editor, documentEntry: NodeEntry<DocumentElement>): void {
  const [document, documentPath] = documentEntry;
  const defaultDocProps = omit(['children'], createDefaultDocElement());
  const propKeys = keys(defaultDocProps);
  const missingKeys = propKeys.some(propKey => !(propKey in document));

  if (!missingKeys) return;

  const newProps = omit(['children'], mergeWithDefaults(document, defaultDocProps));

  Transforms.setNodes(editor, newProps, { at: documentPath });
}
